<template>
  <v-app>
    <v-navigation-drawer
      v-if="user"
      v-model="drawer"
      :permanent="$vuetify.display.mdAndUp"
      :temporary="!$vuetify.display.mdAndUp"
      width="250"
      elevation="0"
      border="r"
      class="bg-grey-lighten-5"
    >
      <div class="pa-4 d-flex align-center border-b mb-2 bg-white" style="height: 65px">
        <v-icon icon="mdi-application-braces-outline" color="grey-darken-1" size="20" class="mr-2" />
        <span class="text-overline font-weight-bold text-grey-darken-1" style="letter-spacing: 1px !important">
          Developer Hub
        </span>
        <v-spacer />
        <v-btn
          v-if="!$vuetify.display.mdAndUp"
          icon="mdi-close"
          variant="text"
          density="comfortable"
          @click="drawer = false"
        />
      </div>

      <v-list density="comfortable" nav class="px-2">
        <v-list-item
          v-for="routeItem in routes"
          :key="routeItem.id"
          :to="routeItem.path"
          :prepend-icon="routeItem.icon"
          :title="routeItem.name"
          color="primary"
          rounded="lg"
          class="mb-1"
        />
      </v-list>

      <template #append>
        <div class="pa-2">
          <v-menu location="top center">
            <template #activator="{ props }">
              <v-list-item v-bind="props" class="rounded-lg border bg-white" link>
                <template #prepend>
                  <v-icon icon="mdi-domain" size="18" color="grey" />
                </template>
                <v-list-item-title class="text-caption font-weight-bold">
                  {{ workspace?.name || 'Select Workspace' }}
                </v-list-item-title>
              </v-list-item>
            </template>
            <v-list density="compact" class="rounded-lg border mb-2">
              <v-list-item prepend-icon="mdi-swap-horizontal" @click="changeWorkspace" title="Change Workspace" />
            </v-list>
          </v-menu>
        </div>
      </template>
    </v-navigation-drawer>

    <v-app-bar
      v-if="user"
      elevation="0"
      border="b"
      color="white"
      flat
      height="64"
    >
      <v-app-bar-nav-icon
        v-if="!$vuetify.display.mdAndUp"
        @click="drawer = !drawer"
      />

      <div class="d-flex align-center px-4 overflow-hidden ga-2">
        <v-breadcrumbs
          v-if="$vuetify.display.smAndUp && breadcrumbs?.length > 1"
          :items="breadcrumbs.slice(0, -1)"
          class="text-caption pa-0 d-none d-sm-flex flex-shrink-0"
          density="compact">
          <template #item="{ item }">
            <v-breadcrumbs-item
              :to="item.path || item.to || item.href"
              class="parent-link text-grey-darken-1"
              link>
              {{ item.title }}
            </v-breadcrumbs-item>
          </template>
          <template #divider>
            <v-icon icon="mdi-chevron-right" size="14" color="grey-lighten-1" />
          </template>
        </v-breadcrumbs>

        <v-icon
          v-if="$vuetify.display.smAndUp && breadcrumbs?.length > 1"
          icon="mdi-chevron-right"
          size="14"
          color="grey-lighten-1"
          class="d-none d-sm-flex ml-n2" />

        <div class="d-flex align-center truncate">
          <v-icon
            :icon="activeSidebarIcon"
            size="20"
            color="primary"
            class="mr-2 d-none d-sm-flex" />
          <h2 class="text-subtitle-1 font-weight-bold truncate tracking-tight">
            {{ breadcrumbs?.[breadcrumbs.length - 1]?.title || routes[0].name }}
          </h2>
        </div>
      </div>

      <v-spacer />

      <v-menu location="bottom end">
        <template #activator="{ props }">
          <v-btn v-bind="props" variant="text" class="px-2">
            <span class="mr-2 text-body-2 font-weight-bold d-none d-md-flex">{{ user.name }}</span>
            <v-avatar
              size="32"
              :style="{'background-color': memberInitials[user.id]?.color || '#eeeeee'}"
              class="border"
            >
              <span class="text-caption font-weight-black">
                {{ memberInitials[user.id]?.initials }}
              </span>
            </v-avatar>
          </v-btn>
        </template>
        <v-list density="compact" class="rounded-lg border mt-2" width="200">
          <v-list-item @click="signOut" prepend-icon="mdi-logout" color="error" title="Logout" />
        </v-list>
      </v-menu>
    </v-app-bar>

    <v-main class="bg-grey-lighten-4">
      <v-container fluid class="pa-0">
        <router-view />
      </v-container>
    </v-main>

    <v-snackbar
      v-model="snackbar.visible"
      :timeout="snackbar.timeout"
      :variant="snackbar.variant"
      :color="snackbar.color">
      {{ snackbar.message }}
      <template #actions>
        <v-btn
          :color="snackbar.closeColor"
          variant="text"
          @click="closeSnackbar">
          Close
        </v-btn>
      </template>
    </v-snackbar>
  </v-app>
</template>

<style scoped>
.truncate {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
<script>
import { mapActions, mapState } from 'redux-vuex';
import { computed, ref, toRefs } from 'vue';
import { useRoute } from 'vue-router';
import { useDisplay } from 'vuetify';

import get from 'lodash/get';
import envConfig from 'brand/env';

import { routes } from '@/router';
import { memberInitials } from '@/selectors';

import {
  getWorkspaceId,
  getChangeWorkspaceLink,
  getYabbrLink,
  findEnv,
} from './utils';

export default {
  name: 'App',
  setup() {
    const { mdAndUp } = useDisplay();
    const drawer = ref(mdAndUp.value);

    const route = useRoute();

    const routeResources = toRefs(mapState(routes
      .map((route) => route.meta && route.meta.breadcrumbResource || '')
      .filter((resource) => resource)));

    const breadcrumbs = computed(() => {
      if (!route || !route.meta || !route.meta.breadcrumb) return [];
      if (!Array.isArray(route.meta.breadcrumb)) return [];

      return route.meta.breadcrumb.map((crumb) => {
        if (typeof crumb.title === 'string') return crumb;

        const resourceId = route.params[crumb.title.param];
        if (!resourceId) return crumb.title.default;
        if (!crumb.title.property) return resourceId;

        const resourceStore = routeResources[route.meta.breadcrumbResource];
        if (!('resources' in resourceStore.value) || !(resourceId in resourceStore.value.resources)) return crumb.title.default;

        return {
          ...crumb,
          title: resourceStore.value.resources[resourceId][crumb.title.property] || crumb.title.default,
        };
      });
    });

    return {
      ...mapActions(
        'endSession',
        'removePopup',
        'setSnackbar',
        'closeSnackbar',
      ),
      ...toRefs(mapState(
        'user',
        'workspaceDetail',
        'popupList',
        'snackbar',
      )),
      ...toRefs(mapState({ memberInitials })),
      breadcrumbs,
      drawer,
    };
  },
  data() {
    return {
      dialog: false,
      dialogItem: {},
      mini: true,
      routes: [
        { id: 'dashboard', name: 'Dashboard', icon: 'mdi-view-dashboard', path: '/' },
        { id: 'workflow', name: 'Workflows', icon: 'mdi-sitemap', path: '/workflows' },
        { id: 'audits', name: 'Workflow Monitor', icon: 'mdi-bug-check-outline', path: '/audits' },
        { id: 'credentials', name: 'Credential Vault', icon: 'mdi-safe', path: '/credentials' },
        { id: 'domains', name: 'Namespaces', icon: 'mdi-connection', path: '/domains' },
        { id: 'schedules', name: 'Scheduled Events', icon: 'mdi-clock-outline', path: '/schedules' },
        { id: 'timeouts', name: 'Timed Conditions', icon: 'mdi-timer-settings-outline', path: '/time-conditions' },
      ],
    };
  },
  computed: {
    env() {
      return envConfig[findEnv(envConfig, window.top.location.origin)];
    },
    workspace() {
      return get(this.workspaceDetail, ['resources', getWorkspaceId()], {});
    },
    activeSidebarIcon() {
      const match = this.routes.toReversed().find(r => this.$route.path.startsWith(r.path));
      return match ? match.icon : null;
    },
  },
  methods: {
    changeWorkspace() {
      localStorage.removeItem('yabbr-organisation');
      window.location.href = getChangeWorkspaceLink();
    },
    signOut() {
      localStorage.removeItem('yabbr-token');
      this.endSession();
      window.location.href = getYabbrLink();
    },
    hasPermission(perms) {
      if (!this.memberList.resources[this?.user?.id]) return false;

      return perms.indexOf(this.memberList.resources[this?.user?.id] !== -1);
    },
    closeDialog() {
      this.dialog = false;
      this.removePopup();
    },
  },
  watch: {
    popupList(newP) {
      if (!newP.length) return;
      this.dialogItem = newP[0];
      this.dialog = true;
    },
  },
};
</script>
<style lang="scss">
body {
  font-family: Open Sans,Roboto,monospace !important;
}

.cursor-pointer {
  cursor: pointer;
}

.theme--light.v-toolbar.v-sheet {
  background: none !important;
}

.theme--light.v-breadcrumbs .v-breadcrumbs__item--disabled {
  color: rgba(0, 0, 0, 0.87) !important;
}

.theme--light.v-application {
  background: rgb(237, 238, 243) !important;
}

.v-sheet.v-card.elevation-2 {
  border-radius: 8px !important;
  box-shadow: 0 5px 8px rgb(52 73 82 / 10%) !important;
}

/* eslint max-len: "off" */
.theme--light.v-data-table
  > .v-data-table__wrapper
  > table
  > tbody
  > tr:hover:not(.v-data-table__expanded__content):not(.v-data-table__empty-wrapper) {
  cursor: pointer;
}

html {
  overflow-y: auto !important;
}

.tracking-tight {
  letter-spacing: -0.02em !important;
}
</style>
